import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query'
import { apiFetch } from '@shared/api/http'
import { toQueryString } from '@shared/api/query'
import type { Currency } from '@shared/contracts/enums'
import { BalanceListSchema, ReleaseDetailSchema, StatementPageSchema } from './wallet.contract'
import type {
  BalanceSummary,
  ReleaseDetail,
  StatementPage,
  StatementQueryParams,
} from './wallet.contract'

export type StatementFilters = Omit<StatementQueryParams, 'cursor'>

export const walletKeys = {
  balance: () => ['wallet', 'balance'] as const,
  statement: (filters?: StatementFilters) =>
    filters ? (['wallet', 'statement', filters] as const) : (['wallet', 'statement'] as const),
  release: (date: string, currency: string) => ['wallet', 'release', date, currency] as const,
}

export async function getWalletBalance(): Promise<BalanceSummary[]> {
  return BalanceListSchema.parse(await apiFetch<unknown>('/wallet/balance'))
}

export function balanceQuery() {
  return queryOptions({
    queryKey: walletKeys.balance(),
    queryFn: getWalletBalance,
    refetchInterval: 15000,
    refetchIntervalInBackground: false,
  })
}

export function useWalletBalanceQuery() {
  return useQuery(balanceQuery())
}

export async function getWalletRelease(
  date: string,
  currency: Currency,
  offset?: number,
): Promise<ReleaseDetail> {
  const query = toQueryString({ currency, limit: 50, offset })
  return ReleaseDetailSchema.parse(
    await apiFetch<unknown>(`/wallet/releases/${date.slice(0, 10)}${query}`),
  )
}

export function releaseInfiniteQuery(date: string | null, currency: Currency | null) {
  const enabled = date !== null && currency !== null
  return infiniteQueryOptions({
    queryKey: walletKeys.release(date ?? '', currency ?? ''),
    queryFn: ({ pageParam }) => getWalletRelease(date!, currency!, pageParam ?? undefined),
    initialPageParam: null as number | null,
    getNextPageParam: (lastPage: ReleaseDetail) => lastPage.nextOffset,
    enabled,
  })
}

export function useWalletReleaseInfiniteQuery(date: string | null, currency: Currency | null) {
  return useInfiniteQuery(releaseInfiniteQuery(date, currency))
}

export async function getWalletStatementPage(params: StatementQueryParams): Promise<StatementPage> {
  const query = toQueryString({
    cursor: params.cursor,
    limit: params.limit,
    presentation: 'movements',
    direction: params.direction,
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
    currency: params.currency,
    q: params.q,
  })
  return StatementPageSchema.parse(await apiFetch<unknown>(`/wallet/statement${query}`))
}

export function statementInfiniteQuery(filters: StatementFilters, enabled = true) {
  return infiniteQueryOptions({
    queryKey: walletKeys.statement(filters),
    queryFn: ({ pageParam }) =>
      getWalletStatementPage({ ...filters, cursor: pageParam ?? undefined }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: StatementPage) => lastPage.nextCursor,
    enabled,
  })
}

export function useWalletStatementInfiniteQuery(filters: StatementFilters, enabled = true) {
  return useInfiniteQuery(statementInfiniteQuery(filters, enabled))
}
